import keyBy from 'lodash/keyBy';
import isEqual from 'lodash/isEqual';
import {
    DxMeasureOption,
    DxProductAreaTagType,
    TimeSeriesInterval,
    DxTargetMeasure,
    Validatable,
} from '@/types/analytics/data-explorer.types';
import { EventName, TimeSeriesPeriod } from '@/types/analytics/analytics.types';

type DxMeasureOptions = {
    [key: string]: DxMeasureOption;
};

/** Spawn branch keys for product-area aggregations. */
export type ProductAreaSpawnEventSource = DxProductAreaTagType;

export const PRODUCT_AREA_TAG_TYPE_LABELS: Record<ProductAreaSpawnEventSource, string> = {
    page: 'Page views',
    guide: 'Guide views',
    feature: 'Feature clicks',
    trackType: 'Track Events',
};

const NON_PRODUCT_AREA_TARGET_TYPES = [
    EventName.App,
    EventName.Page,
    EventName.Feature,
    EventName.TrackEvent,
    EventName.Guide,
    EventName.AIAgent,
];

export const TOTAL_EVENTS_EQUIVALENT_MEASURE_NAMES = new Set<string>(['total']);

export function isTotalEventsEquivalentMeasure (measureName?: string) {
    return !!measureName && TOTAL_EVENTS_EQUIVALENT_MEASURE_NAMES.has(measureName);
}

export function productAreaTagTypesMatch (
    a?: ReadonlyArray<DxProductAreaTagType>,
    b?: ReadonlyArray<DxProductAreaTagType>,
) {
    return isEqual(a ?? [], b ?? []);
}

export const MEASURE_OPTIONS: DxMeasureOptions = {
    TOTAL: {
        name: 'total',
        label: 'Total events',
        description: 'Total number of event interactions that occurred for the selected events or apps',
        group: 'Event metrics',
        disabledTargetTypes: [],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    AVERAGE: {
        name: 'average',
        label: 'Visitor event average',
        description: 'Average number of events that occurred per visitor',
        group: 'Event metrics',
        disabledTargetTypes: [EventName.App, EventName.ProductArea],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    AVERAGE_ACCOUNTS: {
        name: 'averageAccounts',
        label: 'Account event average',
        description: 'Average number of events that occurred per account',
        group: 'Event metrics',
        disabledTargetTypes: [EventName.App, EventName.ProductArea],
        requiredSubFlags: ['hasAccountLevelAnalyticsEnabled'],
        requiresFlagCheck: false,
    },
    MEDIAN: {
        name: 'median',
        label: 'Median events',
        description: 'Median number of events that occurred per active visitor for the selected events or apps',
        group: 'Event metrics',
        disabledTargetTypes: [EventName.App, EventName.ProductArea],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    NUM_MINUTES: {
        name: 'numMinutes',
        label: 'Total time',
        description: 'Total amount of time spent on the selected Pages or apps',
        group: 'Time metrics',
        disabledTargetTypes: [EventName.TrackEvent, EventName.Guide, EventName.Feature, EventName.Email],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    AVERAGE_MINUTES_PER_VISITOR: {
        name: 'averageMinutesPerVisitor',
        label: 'Average time per visitor',
        description: 'Average amount of time spent on the selected Pages or apps per visitor',
        group: 'Time metrics',
        disabledTargetTypes: [EventName.TrackEvent, EventName.Guide, EventName.Feature, EventName.Email],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    AVERAGE_MINUTES_PER_ACCOUNT: {
        name: 'averageMinutesPerAccount',
        label: 'Average time per account',
        description: 'Average amount of time spent on the selected Pages or apps per account',
        group: 'Time metrics',
        disabledTargetTypes: [EventName.TrackEvent, EventName.Guide, EventName.Feature, EventName.Email],
        requiredSubFlags: ['hasAccountLevelAnalyticsEnabled'],
        requiresFlagCheck: false,
    },
    NUM_VISITORS: {
        name: 'numVisitors',
        label: 'Number of unique visitors',
        description: 'Number of unique visitors who used the selected events or apps',
        group: 'Visitor metrics',
        disabledTargetTypes: [],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    NUM_ACCOUNTS: {
        name: 'numAccounts',
        label: 'Number of unique accounts',
        description: 'Number of unique accounts that used the selected events or apps',
        group: 'Account metrics',
        disabledTargetTypes: [],
        requiredSubFlags: ['hasAccountLevelAnalyticsEnabled'],
        requiresFlagCheck: false,
    },
    NUM_SUBJECTS: {
        name: 'numSubjects',
        label: 'Total values',
        description:
            'Total number of distinct values for a selected object property, event property, or historical metadata field. ' +
            'Rows without a value on that field are excluded.',
        group: 'Property metrics',
        disabledTargetTypes: [],
        requiredSubFlags: [],
        requiresFlagCheck: false,
        requiresSubject: true,
        requiredCapabilities: ['objectAnalytics', 'middlewareBuilder'],
    },
    PERCENT_VISITORS: {
        name: 'percentVisitors',
        label: 'Percent of visitors',
        description: 'Percentage of visitors in your subscription who used the selected events or apps',
        group: 'Visitor metrics',
        disabledTargetTypes: [EventName.App, EventName.Guide, EventName.ProductArea],
        requiredSubFlags: [],
        requiresFlagCheck: true,
    },
    PERCENT_ACCOUNTS: {
        name: 'percentAccounts',
        label: 'Percent of accounts',
        description: 'Percentage of accounts in your subscription that used the selected events or apps',
        group: 'Account metrics',
        disabledTargetTypes: [EventName.App, EventName.Guide, EventName.ProductArea],
        requiredSubFlags: ['hasAccountLevelAnalyticsEnabled'],
        requiresFlagCheck: true,
    },
    ERROR_CLICKS: {
        name: 'errorClicks',
        label: 'Error clicks',
        description:
            'The number of clicks that generated an error message that occurred for the selected events or apps',
        group: 'Frustration metrics',
        disabledTargetTypes: [EventName.Guide, EventName.TrackEvent, EventName.Email],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    RAGE_CLICKS: {
        name: 'rageClicks',
        label: 'Rage clicks',
        description:
            'The number of repetitive clicks on a specific element that occurred for the selected events or apps',
        group: 'Frustration metrics',
        disabledTargetTypes: [EventName.Guide, EventName.TrackEvent, EventName.Email],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    U_TURNS: {
        name: 'uTurns',
        label: 'U-turns',
        description:
            'The number of instances a user left a page within seven seconds that occurred for the selected events or apps',
        group: 'Frustration metrics',
        disabledTargetTypes: [EventName.Guide, EventName.TrackEvent, EventName.Feature, EventName.Email],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    DEAD_CLICKS: {
        name: 'deadClicks',
        label: 'Dead clicks',
        description: 'The number of clicks that have no effect that occurred for the selected events or apps',
        group: 'Frustration metrics',
        disabledTargetTypes: [EventName.Guide, EventName.TrackEvent, EventName.Email],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    PRODUCT_AREA_PAGE_VIEWS: {
        name: 'total',
        tagTypes: ['page'],
        label: 'Page views',
        description: 'Page view events within the selected product areas',
        group: 'Product area metrics',
        disabledTargetTypes: NON_PRODUCT_AREA_TARGET_TYPES,
        requiredSubFlags: [],
        requiresFlagCheck: false,
        requiresProductArea: true,
    },
    PRODUCT_AREA_GUIDE_VIEWS: {
        name: 'total',
        tagTypes: ['guide'],
        label: 'Guide views',
        description: 'Guide view events within the selected product areas',
        group: 'Product area metrics',
        disabledTargetTypes: NON_PRODUCT_AREA_TARGET_TYPES,
        requiredSubFlags: [],
        requiresFlagCheck: false,
        requiresProductArea: true,
    },
    PRODUCT_AREA_FEATURE_CLICKS: {
        name: 'total',
        tagTypes: ['feature'],
        label: 'Feature clicks',
        description: 'Feature click events within the selected product areas',
        group: 'Product area metrics',
        disabledTargetTypes: NON_PRODUCT_AREA_TARGET_TYPES,
        requiredSubFlags: [],
        requiresFlagCheck: false,
        requiresProductArea: true,
    },
    PRODUCT_AREA_TRACK_EVENTS: {
        name: 'total',
        tagTypes: ['trackType'],
        label: 'Track Events',
        description: 'Track Events within the selected product areas',
        group: 'Product area metrics',
        disabledTargetTypes: NON_PRODUCT_AREA_TARGET_TYPES,
        requiredSubFlags: [],
        requiresFlagCheck: false,
        requiresProductArea: true,
    },
};

export function findMeasureOption (
    measureName?: string,
    tagTypes?: ReadonlyArray<DxProductAreaTagType>,
): DxMeasureOption | undefined {
    return Object.values(MEASURE_OPTIONS).find((option) =>
        option.name === measureName && productAreaTagTypesMatch(option.tagTypes, tagTypes),
    );
}

export function getMeasureOptionKey (
    measureName?: string,
    tagTypes?: ReadonlyArray<DxProductAreaTagType>,
): string | undefined {
    if (!measureName) {
        return undefined;
    }

    return tagTypes?.length ? `${measureName}:${tagTypes.join(',')}` : measureName;
}

const measureOptionsByName = keyBy(
    Object.values(MEASURE_OPTIONS).filter((option) => !option.tagTypes?.length),
    'name',
);

export const getLabelFromTargetGroupMeasure = (
    measure?: Validatable<DxTargetMeasure>,
    tagTypes?: ReadonlyArray<DxProductAreaTagType>,
) => {
    if (!measure || !measure.value.name) {
        return '';
    }

    const matchedOption = findMeasureOption(measure.value.name, tagTypes);
    if (matchedOption) {
        return matchedOption.label;
    }

    return measureOptionsByName[measure.value.name]?.label;
};

type TimeIntervalOption = {
    name: TimeSeriesPeriod;
    label: string;
    interval: TimeSeriesInterval;
};

export const TIME_INTERVAL_OPTIONS: TimeIntervalOption[] = [
    {
        name: TimeSeriesPeriod.Hourly,
        label: 'Hourly',
        interval: 'hour',
    },
    {
        name: TimeSeriesPeriod.Daily,
        label: 'Daily',
        interval: 'day',
    },
    {
        name: TimeSeriesPeriod.Weekly,
        label: 'Weekly',
        interval: 'week',
    },
    {
        name: TimeSeriesPeriod.Monthly,
        label: 'Monthly',
        interval: 'month',
    },
];
